import "../../css/GlobalStyles.css";
import { Icon } from "@iconify/react";
import { CSSProperties, memo, useCallback, useState } from "react";
import { Colors } from "../../utils/colors";
import { FileUploader } from "react-drag-drop-files";
import {
  errorMediaAlertText,
  errorMediaSubAlertText,
} from "../../utils/mediaUtils";
import {
  errorAlertText,
  errorSubAlertText,
  MAX_IMAGE_MEDIA_SIZE_TEXT,
  MEDIA_UPLOAD_CODES,
  uploadMedia,
} from "../../utils/photoUtils";
import { ThemeStyle } from "../../hooks/useTheme";
import AlertContainer from "../Containers/AlertContainer";

type MediaUploadFileProps = {
  onUpload: (blobUrl: string, fileName?: string) => void;
  supportedFileTypes: string[];
  theme: ThemeStyle;
  disabled?: boolean;
  containerStyles?: CSSProperties;
};

// The upload file container to import or drag and drop a file
const MediaUploadFile = memo(function MediaUploadFileFn(
  props: MediaUploadFileProps
) {
  const { onUpload, supportedFileTypes, theme, disabled, containerStyles } =
    props;
  const [uploadLoading, setUploadLoading] = useState(false);
  const [alertText, setAlertText] = useState<{
    header: string;
    subHeader: string;
  }>({ header: "", subHeader: "" });

  const uploadFile = useCallback(
    (file: File) => {
      return new Promise<void>((resolve, reject) => {
        (async () => {
          setUploadLoading(true);

          // For non-image files (e.g. PDF), keep the original file as a blob URL.
          if (!file.type.startsWith("image/")) {
            if (file.size > MAX_IMAGE_MEDIA_SIZE_TEXT) {
              setAlertText({
                header: errorAlertText(MEDIA_UPLOAD_CODES.TOO_BIG),
                subHeader: errorSubAlertText(MEDIA_UPLOAD_CODES.TOO_BIG),
              });
              setUploadLoading(false);
              resolve();
              return;
            }
            onUpload(URL.createObjectURL(file), file.name);
            setUploadLoading(false);
            resolve();
            return;
          }

          const uploadData = await uploadMedia(file, false);
          if (uploadData.uploadCode !== MEDIA_UPLOAD_CODES.SUCCESS) {
            setAlertText({
              header: errorAlertText(uploadData.uploadCode),
              subHeader: errorSubAlertText(uploadData.uploadCode),
            });
            setUploadLoading(false);
            resolve();
            return;
          }

          onUpload(uploadData.blobUrl, file.name);
          resolve();
          setUploadLoading(false);
        })().catch((error) => {
          setUploadLoading(false);
          reject(error);
        });
      });
    },
    [onUpload]
  );

  return (
    <>
      <FileUploader
        handleChange={uploadFile}
        name="file"
        types={supportedFileTypes}
        onTypeError={() =>
          setAlertText({
            header: errorMediaAlertText(MEDIA_UPLOAD_CODES.INVALID),
            subHeader: errorMediaSubAlertText(
              MEDIA_UPLOAD_CODES.INVALID,
              supportedFileTypes
            ),
          })
        }
        dropMessageStyle={{ opacity: 0.5 }}
        disabled={uploadLoading || disabled}
      >
        <div
          className="ColumnCentering"
          style={{
            height: 90,
            borderRadius: 20,
            border: `1px solid ${Colors.GRAY2}`,
            borderStyle: "dashed",
            gap: 7,
            cursor: "pointer",
            ...containerStyles,
          }}
        >
          <div className="AlignedRow" style={{ gap: 4 }}>
            <Icon
              icon="ic:baseline-upload-file"
              height={22}
              style={{ color: theme.PrimaryText.color }}
            />
            <span className="bodyMedium" style={theme.PrimaryText}>
              Upload File
            </span>
          </div>
          <div className="ColumnCentering" style={{ gap: 4 }}>
            <span className="smallBodySubtext">
              Supported formats are {supportedFileTypes.join(", ")}
            </span>
            <span className="smallBodySubtext">
              Size limit: {MAX_IMAGE_MEDIA_SIZE_TEXT / 1000000}
              MB
            </span>
          </div>
        </div>
      </FileUploader>
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
      />
    </>
  );
});

export default MediaUploadFile;
